import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { ProxyHealthStuckCommand } from "./proxyHealthTypes";

interface StuckCommandDetailModalProps {
  open: boolean;
  onClose: () => void;
  command: ProxyHealthStuckCommand | null;
  locationName: string | null;
}

function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : iso;
}

function formatAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.fromNow() : iso;
}

export default function StuckCommandDetailModal({
  open,
  onClose,
  command,
  locationName,
}: StuckCommandDetailModalProps) {
  const locationLabel = command
    ? locationName || command.locationApiId
    : "";

  return (
    <Dialog
      open={open && !!command}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      {command ? (
        <>
          <DialogTitle>
            <span>Stuck command #{command.id}</span>
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <dl className="sq-dl-rows">
              <div>
                <dt>Command</dt>
                <dd>
                  <code>{command.command}</code>
                </dd>
              </div>
              <div>
                <dt>Machine</dt>
                <dd>
                  <code>{command.macId}</code>
                </dd>
              </div>
              <div>
                <dt>Location</dt>
                <dd>
                  {locationLabel}
                  {locationName && locationName !== command.locationApiId ? (
                    <div className="sq-mono">{command.locationApiId}</div>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{command.status}</dd>
              </div>
              <div>
                <dt>Attempts</dt>
                <dd>{command.attempts}</dd>
              </div>
              <div>
                <dt>Alliance command ID</dt>
                <dd>
                  {command.allianceCommandId ? (
                    <code>{command.allianceCommandId}</code>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              <div>
                <dt>Last error</dt>
                <dd className={command.lastError ? "sq-value-bad" : undefined}>
                  {command.lastError || "—"}
                </dd>
              </div>
              <div>
                <dt>Requested by</dt>
                <dd>{command.requestedBy || "—"}</dd>
              </div>
              <div>
                <dt>Client</dt>
                <dd>{command.client || "—"}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd title={formatAbsolute(command.createdAt)}>
                  {formatAgo(command.createdAt)}
                  <div className="sq-muted">{formatAbsolute(command.createdAt)}</div>
                </dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd title={formatAbsolute(command.updatedAt)}>
                  {formatAgo(command.updatedAt)}
                  <div className="sq-muted">{formatAbsolute(command.updatedAt)}</div>
                </dd>
              </div>
            </dl>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}

export function stuckAttemptsTooltip(row: ProxyHealthStuckCommand): string {
  const parts = [
    `Status: ${row.status}`,
    `Attempts: ${row.attempts}`,
    `Age: ${formatAgo(row.createdAt)}`,
  ];
  if (row.allianceCommandId) {
    parts.push(`Alliance ID: ${row.allianceCommandId}`);
  }
  if (row.lastError) {
    const err =
      row.lastError.length > 120 ? `${row.lastError.slice(0, 120)}…` : row.lastError;
    parts.push(`Last error: ${err}`);
  } else {
    parts.push("Last error: none");
  }
  parts.push("Click for full details");
  return parts.join("\n");
}
